import { cheaper } from './prices.ts';
import { NPC_KIN } from './kin.ts';
import type { Skill } from './content/types.ts';
import { advance, content, dayOf, earn, spend, startLife, type LifeEvent, type LifeState, money } from './life.ts';
import { Rng } from './rng.ts';
import { homeCountry, USD_NGN, type Country } from './travel.ts';
import { hereCountry } from './character.ts';
import { voiceOf } from './voice.ts';
import { localPrice } from './character.ts';
import { generateLife, type LifeCard } from './spawn.ts';

/**
 * The longer arcs of a life: owning a business, falling for someone, getting older, dying, and
 * someone picking up what you left. Time here is compressed: a year passes every DAYS_PER_YEAR
 * game days, so a life plays out over weeks of real play, not decades.
 */

export const DAYS_PER_YEAR = 28;
const DAY = 1440;
const clamp = (v: number) => Math.max(0, Math.min(100, v));
const rngFor = (s: LifeState, salt: number) => new Rng((s.seed ^ Math.imul(salt + 7, 0x85ebca6b)) >>> 0);
const naira = (s: LifeState, n: number) => money(s, n);

// --- age ------------------------------------------------------------------------------------------

/** Nobody ages (the user, 2026-10-28): you stay the age you started, and everyone is young, 25 at most (a life
 * begun older, before this, is 25 again). DAYS_PER_YEAR is still the length of a year for everything else. */
export const YOUTH_MAX = 25;
export function ageOf(s: LifeState): number { return Math.min(YOUTH_MAX, s.card.age); }

/** Called once a day: now and then an illness (no birthdays, no ageing: see ageOf). */
export function ageDay(s: LifeState, events: LifeEvent[]) {
  const day = dayOf(s.minutes);
  const r = rngFor(s, day * 17 + 1);
  // now and then you fall ill (more often when run down)
  if (r.chance(0.006 + (s.health < 40 ? 0.02 : 0))) {
    s.health = clamp(s.health - r.range(8, 22));
    events.push({ t: s.minutes, kind: 'need', text: r.pick(['You woke up with malaria. Get to a pharmacy.', 'A bad fever. You feel terrible.', 'Typhoid, the chemist thinks. Rest and medicine.']) });
  }
  // (no dying of old age or illness: a character never dies, by decision; a player whose character died lost every
  // car and house with it. The illnesses above still happen)
}

export function die(s: LifeState, events: LifeEvent[], cause: string) {
  if (s.dead) return;
  const age = ageOf(s);
  s.dead = { t: s.minutes, age, cause };
  s.history.push({ t: s.minutes, text: `Died at ${age}, of ${cause}.` });
  events.push({ t: s.minutes, kind: 'death', text: `${s.card.name} died at ${age}.` });
}

// --- business --------------------------------------------------------------------------------------

export interface BusinessDef { id: string; label: string; price: number; revenue: [number, number]; costs: number; skill: Skill; wage: number; blurb: string; /** the countries it's sold in (none: Nigeria) */ where?: Country[] }

/** (abroad, prices in dollars: a day's takings, costs and wages, and what it costs to buy) */
const U = USD_NGN;
const usd = (id: string, label: string, where: Country[], price: number, rev: [number, number], costs: number, wage: number, skill: Skill, blurb: string): BusinessDef =>
  ({ id, label, where, price: price * U, revenue: [rev[0] * U, rev[1] * U], costs: costs * U, wage: wage * U, skill, blurb });

export const BUSINESSES: BusinessDef[] = [
  { id: 'pos', label: 'POS stand', price: 350_000, revenue: [7_000, 16_000], costs: 1_500, skill: 'business', wage: 2_500, blurb: 'An umbrella, a stool and a POS machine. Withdrawals, transfers, airtime.' },
  { id: 'provisions', label: 'Provisions kiosk', price: 900_000, revenue: [16_000, 34_000], costs: 8_000, skill: 'business', wage: 3_500, blurb: 'A container kiosk stocked with everything a street needs.' },
  { id: 'buka', label: 'Buka', price: 1_600_000, revenue: [28_000, 62_000], costs: 17_000, skill: 'cooking', wage: 5_000, blurb: 'Rice, swallow and soup for the street at lunchtime. The food has to be good.' },
  { id: 'salon', label: 'Hair salon', price: 2_200_000, revenue: [30_000, 75_000], costs: 14_000, skill: 'creativity', wage: 6_000, blurb: 'Two chairs, a dryer, braids and weaves; Saturday is the whole week.' },
  { id: 'barber', label: 'Barbing salon', price: 800_000, revenue: [14_000, 32_000], costs: 5_000, skill: 'creativity', wage: 3_500, blurb: 'Clippers, a mirror, a generator and the football on the TV.' },
  { id: 'carwash', label: 'Car wash', price: 1_400_000, revenue: [20_000, 52_000], costs: 9_000, skill: 'business', wage: 5_000, blurb: 'A pressure washer, four boys, a corner lot on a busy road. Rainy season is slow.' },
  { id: 'lounge', label: 'Lounge and bar', price: 9_000_000, revenue: [90_000, 320_000], costs: 70_000, skill: 'social', wage: 15_000, blurb: 'Small chops, shisha, cold drinks and a DJ on Fridays. The crowd follows the vibe.' },
  { id: 'boutique', label: 'Fashion boutique', price: 3_500_000, revenue: [35_000, 110_000], costs: 20_000, skill: 'creativity', wage: 6_000, blurb: 'Ready-to-wear and ankara pieces; your Instagram is your shop window.' },
  { id: 'dispatch', label: 'Dispatch riders (logistics)', price: 4_500_000, revenue: [45_000, 140_000], costs: 30_000, skill: 'business', wage: 9_000, blurb: 'Five bikes delivering for online shops across the mainland. Traffic is the enemy.' },
  // the China ⇄ Lagos trade: buy in Guangzhou and Yiwu, ship a container to Apapa, sell from a shop at Trade Fair
  { id: 'import', label: 'Import business (China → Lagos)', price: 12_000_000, revenue: [60_000, 520_000], costs: 110_000, skill: 'business', wage: 15_000,
    blurb: "A shop at Trade Fair, a supplier in Guangzhou, a container on the water. Phones, fabric, generators: whatever moves. Big money, big risks. Suppliers trust you more once you've been to China." },
  // France
  usd('cafe', 'Café-bistro', ['france'], 140_000, [700, 1_900], 520, 160, 'cooking', 'A zinc counter, eight tables on the pavement, croque-monsieurs and a steady morning crowd.'),
  usd('boulangerie', 'Boulangerie', ['france'], 180_000, [800, 2_000], 560, 170, 'cooking', 'Up at four for the baguettes; the queue out of the door at eight is your reward.'),
  usd('winebar', 'Wine bar', ['france', 'italy'], 220_000, [900, 2_800], 800, 190, 'social', 'Natural wines by the glass, a cheese board, candles; full on Thursdays.'),
  usd('fromagerie', 'Fromagerie', ['france'], 160_000, [650, 1_700], 480, 160, 'business', 'Two hundred cheeses, and the customers who want to talk about every one of them.'),
  // Italy
  usd('gelateria', 'Gelateria', ['italy'], 120_000, [500, 1_800], 420, 140, 'cooking', 'Twenty-four flavours made in the back each morning. Summer pays for winter.'),
  usd('trattoria', 'Trattoria', ['italy'], 260_000, [1_000, 3_000], 950, 200, 'cooking', 'Red tablecloths, pasta made by hand, a menu written on the board every day.'),
  usd('bar-it', 'Bar (caffè)', ['italy'], 110_000, [500, 1_400], 380, 140, 'social', 'Espresso standing at the counter, cornetti in the morning, aperitivo at six.'),
  usd('agriturismo', 'Agriturismo (farm stay)', ['italy'], 900_000, [1_800, 6_500], 2_200, 380, 'business', 'A farmhouse in the hills with rooms, your own wine and olive oil, guests from everywhere.'),
  // the United States
  usd('foodtruck', 'Food truck', ['usa'], 90_000, [500, 1_700], 360, 150, 'cooking', 'A truck, a menu of six things, and a new street corner every day.'),
  usd('coffee', 'Coffee shop', ['usa', 'uae'], 200_000, [800, 2_300], 620, 180, 'business', 'Single-origin pour-overs, oat milk, laptops at every table till four.'),
  usd('laundromat', 'Laundromat', ['usa'], 250_000, [600, 1_500], 300, 120, 'business', 'Forty machines that earn while you sleep; you fix them when they don\'t.'),
  usd('bodega', 'Bodega', ['usa'], 180_000, [700, 1_900], 560, 150, 'business', 'Sandwiches, lottery tickets, a cat on the counter; open twenty-four hours.'),
  usd('barbershop', 'Barbershop', ['usa'], 120_000, [500, 1_400], 300, 150, 'creativity', 'Four chairs, fades and line-ups, the conversation the real product.'),
  // Japan
  usd('ramen', 'Ramen bar', ['japan'], 160_000, [700, 2_000], 540, 160, 'cooking', 'Ten stools at a counter, one broth simmered for eighteen hours. The line forms at eleven.'),
  usd('izakaya', 'Izakaya', ['japan'], 240_000, [900, 2_700], 850, 190, 'social', 'Lanterns outside, yakitori and cold beer inside; salarymen until the last train.'),
  usd('kissaten', 'Kissaten (coffee house)', ['japan'], 100_000, [350, 1_100], 280, 120, 'cooking', 'Siphon coffee, thick toast, jazz records, and the same regulars every morning.'),
  usd('vending', 'Vending machine route', ['japan'], 60_000, [220, 600], 90, 60, 'business', 'Twelve machines on twelve corners: drinks hot and cold, restocked on your rounds.'),
  // China
  usd('milktea', 'Milk tea shop', ['china'], 70_000, [400, 1_300], 260, 90, 'business', 'Bubble tea with brown sugar pearls; orders from the delivery apps all day long.'),
  usd('noodles', 'Noodle shop', ['china'], 80_000, [380, 1_200], 280, 90, 'cooking', 'Hand-pulled noodles slapped on the counter while the customers watch.'),
  usd('estore', 'Online store (livestream selling)', ['china'], 50_000, [200, 2_400], 300, 120, 'social', 'A ring light, a stockroom and three hours live every evening: charm sells.'),
  usd('phonerepair', 'Phone repair stall', ['china'], 25_000, [140, 420], 60, 50, 'technical', 'Screens, batteries, water damage, in an hour while they wait.'),
  // the United Arab Emirates
  usd('shawarma', 'Shawarma stand', ['uae'], 90_000, [500, 1_600], 340, 130, 'cooking', 'The spit turning by the window, garlic sauce, a queue at midnight after the malls close.'),
  usd('oud', 'Perfume shop (oud)', ['uae'], 300_000, [900, 3_800], 900, 200, 'social', 'Oud, musk and rose in crystal bottles; tourists, collectors, and wedding orders.'),
  usd('carhire', 'Luxury car hire', ['uae'], 1_500_000, [3_000, 12_000], 3_800, 400, 'business', 'Four supercars for tourists who want a photo outside the needle tower. Insurance is a nightmare.'),
];
for (const b of BUSINESSES) b.price = cheaper(b.price, 100_000); // (prices 30% down: prices.ts)

/** What's for sale where you are (you buy in the country you're in; it trades there). */
export function businessesHere(s: LifeState): BusinessDef[] { const c = hereCountry(s); return BUSINESSES.filter((d) => (d.where ?? ['nigeria']).includes(c)); }

export type PriceLevel = 'low' | 'fair' | 'high';
export interface Business { id: string; label: string; price: PriceLevel; staffed: boolean; since: number; week: number[]; lastWorkedDay: number; boughtFor: number; /** where it is (older lives: Nigeria) */ country?: Country }

export function buyBusiness(s: LifeState, id: string): LifeEvent[] | null {
  const d = businessesHere(s).find((b) => b.id === id);
  if (!d || s.business || !spend(s, d.price, `Bought: ${d.label}`)) return null;
  s.business = { id, label: d.label, price: 'fair', staffed: false, since: s.minutes, week: [], lastWorkedDay: -1, boughtFor: d.price, country: hereCountry(s) };
  s.history.push({ t: s.minutes, text: `Bought a ${d.label.toLowerCase()} for ${naira(s, d.price)}.` });
  return [{ t: s.minutes, kind: 'info', text: `It's yours: a ${d.label.toLowerCase()}. Work it yourself, or hire someone to run it.` }, ...advance(s, 30)];
}

export function sellBusiness(s: LifeState): number {
  if (!s.business) return 0;
  const price = Math.round(s.business.boughtFor * (0.55 + Math.min(0.3, s.business.week.reduce((a, b) => a + b, 0) / s.business.boughtFor)) / 1000) * 1000;
  earn(s, price, `Sold: ${s.business.label}`);
  s.history.push({ t: s.minutes, text: `Sold the ${s.business.label.toLowerCase()} for ${naira(s, price)}.` });
  s.business = null;
  return price;
}

/** Work a shift at your own business: it does much better with you there, and you learn. */
export function workBusiness(s: LifeState): { events: LifeEvent[]; summary: string } | null {
  if (!s.business || s.business.lastWorkedDay === dayOf(s.minutes) || (s.business.country ?? 'nigeria') !== hereCountry(s)) return null;
  s.business.lastWorkedDay = dayOf(s.minutes);
  const d = BUSINESSES.find((b) => b.id === s.business!.id)!;
  const events = advance(s, 8 * 60);
  s.skills[d.skill] = Math.min(100, s.skills[d.skill] + 0.6 * (1 - s.skills[d.skill] / 120));
  s.needs.social = clamp(s.needs.social + 12);
  return { events, summary: `You spent the day at your ${d.label.toLowerCase()}. Customers came and went; you know their names now.` };
}

/** The day's takings, settled overnight. Better with you there, a fair price, and skill. */
export function businessDay(s: LifeState, events: LifeEvent[]) {
  const b = s.business;
  if (!b) return;
  const d = BUSINESSES.find((x) => x.id === b.id)!;
  const yesterday = dayOf(s.minutes) - 1;
  const r = rngFor(s, yesterday * 29 + 3);
  const present = b.lastWorkedDay === yesterday;
  const open = present || b.staffed;
  let profit = -Math.round(d.costs * 0.3); // rent and spoilage even when shut
  if (open) {
    const volume = { low: 1.25, fair: 1, high: 0.72 }[b.price];
    const margin = { low: 0.82, fair: 1, high: 1.22 }[b.price];
    const skill = 0.7 + s.skills[d.skill] / 150;
    const revenue = r.range(d.revenue[0], d.revenue[1]) * volume * margin * skill * (present ? 1.15 : 0.85);
    profit = Math.round((revenue - d.costs - (b.staffed && !present ? d.wage : 0)) / 100) * 100;
    // importing: going to China yourself pays (the suppliers know your face), and the port and the naira bite
    if (b.id === 'import') {
      if (s.history.some((h) => /^Flew to (Shanghai|Beijing)/.test(h.text))) profit = Math.round(profit * 1.35 / 100) * 100;
      if (r.chance(0.035)) { profit -= 1_500_000; events.push({ t: s.minutes, kind: 'info', text: 'Customs at Apapa held your container for "documentation". ₦1,500,000 in charges and a week of empty shelves.' }); }
      else if (r.chance(0.03)) { profit -= 600_000; events.push({ t: s.minutes, kind: 'info', text: 'The naira slid against the yuan overnight. Your next order costs ₦600,000 more.' }); }
      else if (r.chance(0.04)) { profit += 400_000; events.push({ t: s.minutes, kind: 'pay', text: 'A wholesaler from Onitsha bought half the container in one go. ₦400,000 extra.' }); }
    }
    // Lagos happens
    else if ((b.country ?? 'nigeria') === 'nigeria') {
      if (r.chance(0.06)) { profit -= 5_000; events.push({ t: s.minutes, kind: 'info', text: 'Area boys came round to your business for "settlement". ₦5,000 gone.' }); }
      else if (r.chance(0.08)) { profit -= 3_000; events.push({ t: s.minutes, kind: 'info', text: 'No light all day: the generator drank ₦3,000 of fuel.' }); }
    }
    // (and everywhere else: a good day, a bad one)
    else if (r.chance(0.05)) { const x = Math.round(d.costs * 1.5 / 100) * 100; profit -= x; events.push({ t: s.minutes, kind: 'info', text: `${({ france: 'An inspection from the mairie', italy: 'A visit from the Guardia di Finanza', usa: 'A health inspection', japan: 'A broken compressor', china: 'A rent rise from the landlord', uae: 'A licence renewal fee' } as Record<string, string>)[b.country!] ?? 'An unexpected bill'} at your ${b.label.toLowerCase()}: ${money(s, x)}.` }); }
    else if (r.chance(0.05)) { const x = Math.round(d.revenue[1] * 0.6 / 100) * 100; profit += x; events.push({ t: s.minutes, kind: 'pay', text: `Your ${b.label.toLowerCase()} got written up online and the queue was round the block: ${money(s, x)} extra.` }); }
  }
  if (profit >= 0) earn(s, profit, `${b.label} takings`);
  else spend(s, Math.min(-profit, s.money), `${b.label} costs`);
  b.week = [...b.week.slice(-6), profit];
}

// --- relationships ----------------------------------------------------------------------------------

const PLACES = ['for suya on Adeniran Street', 'to a lounge in Surulere', 'to the cinema at Leisure Mall', 'to Bar Beach', 'for small chops and drinks', 'to watch a match at the viewing centre'];
export const isFamily = (rel: string) => ['Mother', 'Father', 'Brother', 'Sister', 'Son', 'Daughter'].includes(rel);
export const isPartner = (rel: string) => ['Girlfriend', 'Boyfriend', 'Partner', 'Wife', 'Husband'].includes(rel);

/** Spend an evening with someone. Costs money and time; brings you closer. */
export function hangOut(s: LifeState, name: string, quality = 0.6, where?: string): { ok: boolean; line: string; events: LifeEvent[] } {
  const p = s.contacts.find((c) => c.name === name);
  const r = rngFor(s, s.minutes + name.length);
  const cost = localPrice(s, r.int(2, 6) * 1000, 1000); // (a Lagos night out, at this place's prices)
  if (!p || !spend(s, cost, 'Going out')) return { ok: false, line: 'You can\'t afford a night out right now.', events: [] };
  const place = where ?? r.pick(PLACES);
  const q = Math.max(0, Math.min(1, quality));
  // closer is slower; how you actually were with them matters more than where you went
  const gain = (r.range(5, 11) + s.skills.social / 20) * (1 - p.closeness / 130) * (0.3 + q * 1.2) - (q < 0.3 ? 4 : 0);
  p.closeness = Math.max(0, Math.min(100, p.closeness + gain));
  p.seen = s.minutes;
  s.needs.social = clamp(s.needs.social + 25);
  s.needs.fun = clamp(s.needs.fun + 20);
  s.skills.social = Math.min(100, s.skills.social + 0.3 + q * 0.4);
  const first = name.split(' ')[0];
  const how = q < 0.3 ? 'It was awkward. You both checked your phones more than you talked.' : p.closeness > 70 ? 'It felt easy, like you\'ve known each other for years.' : q > 0.75 ? 'A really good evening.' : 'A good evening.';
  return { ok: true, line: `You and ${first} went ${place}. ${how}`, events: advance(s, 150) };
}

/** Ask someone out. They might say yes. */
export function askOut(s: LifeState, name: string): { yes: boolean; line: string; events: LifeEvent[] } {
  if (!NPC_KIN) return { yes: false, line: '…', events: [] }; // (NPC romance: archived, kin.ts)
  const p = s.contacts.find((c) => c.name === name);
  const r = rngFor(s, s.minutes * 3 + name.length);
  const events = advance(s, 10);
  if (!p || isFamily(p.relation) || isPartner(p.relation)) return { yes: false, line: '…', events };
  if (s.contacts.some((c) => isPartner(c.relation))) return { yes: false, line: 'You\'re already seeing someone.', events };
  const chance = (p.closeness - 45) / 60 + s.skills.social / 300 + (s.dressedDay === dayOf(s.minutes) ? 0.05 : 0);
  const yes = r.chance(Math.max(0.03, Math.min(0.9, chance)));
  if (yes) {
    p.relation = s.card.sex === 'm' ? 'Girlfriend' : 'Boyfriend';
    p.closeness = Math.min(100, p.closeness + 10);
    s.history.push({ t: s.minutes, text: `Started dating ${name}.` });
  } else p.closeness = Math.max(0, p.closeness - 8);
  return { yes, line: yes ? r.pick(['"I was wondering when you\'d ask."', '"Yes. Obviously yes."', '"Okay… yes. Let\'s see where this goes."']) : r.pick(['"I like you, but not like that."', '"I\'m not ready for anything right now."', voiceOf(homeCountry(s)).friendsOnly]), events };
}

/** Propose. A ring, a question, an answer. */
export function propose(s: LifeState, name: string): { yes: boolean; line: string; events: LifeEvent[] } {
  if (!NPC_KIN) return { yes: false, line: '…', events: [] }; // (NPC romance: archived, kin.ts)
  const p = s.contacts.find((c) => c.name === name);
  const ring = cheaper(150_000, 5_000);
  if (!p || !['Girlfriend', 'Boyfriend', 'Partner'].includes(p.relation)) return { yes: false, line: '…', events: [] };
  if (!spend(s, ring, 'Engagement ring')) return { yes: false, line: `You need a ring first: about ₦${ring.toLocaleString()}.`, events: [] };
  const r = rngFor(s, s.minutes * 5 + 11);
  const together = (s.minutes - (s.history.find((h) => h.text === `Started dating ${name}.`)?.t ?? s.minutes)) / DAY;
  const yes = r.chance(Math.max(0.05, Math.min(0.95, (p.closeness - 60) / 35 + (together > DAYS_PER_YEAR / 2 ? 0.15 : -0.2))));
  const events = advance(s, 60);
  if (yes) {
    p.relation = s.card.sex === 'm' ? 'Wife' : 'Husband';
    p.closeness = 100;
    s.history.push({ t: s.minutes, text: `Married ${name}.` });
    events.push({ t: s.minutes, kind: 'info', text: `You and ${name.split(' ')[0]} are married.` });
  } else { p.closeness = Math.max(0, p.closeness - 20); earn(s, Math.round(ring * 0.6), 'Ring returned (part refund)'); }
  return { yes, line: yes ? '"Yes! Yes, of course!"' : '"I… I\'m not ready. I\'m sorry."', events };
}

/** Married couples can try for a child; the pregnancy takes 9 months of game time. */
export function tryForBaby(s: LifeState): { line: string; events: LifeEvent[] } {
  if (!NPC_KIN) return { line: 'Not now.', events: [] }; // (a baby would be a game-made child: archived with NPC family, kin.ts)
  const spouse = s.contacts.find((c) => c.relation === 'Wife' || c.relation === 'Husband');
  if (!spouse) return { line: 'You\'re not married.', events: [] };
  if (s.pregnancy) return { line: 'A baby is already on the way.', events: [] };
  const r = rngFor(s, s.minutes + 99);
  const events = advance(s, 60);
  if (r.chance(0.3)) {
    s.pregnancy = { due: s.minutes + Math.round((9 / 12) * DAYS_PER_YEAR * DAY) };
    return { line: 'A few weeks later: you\'re expecting a baby.', events: [...events, { t: s.minutes, kind: 'info', text: 'You\'re expecting a baby.' }] };
  }
  return { line: 'Not this time.', events };
}

/** Daily: relationships cool if neglected; a baby arrives when it's due. */
export function relationsDay(s: LifeState, events: LifeEvent[]) {
  for (const c of s.contacts) if (isPartner(c.relation)) c.closeness = Math.max(0, c.closeness - 0.6);
  const partner = s.contacts.find((c) => isPartner(c.relation) && !['Wife', 'Husband'].includes(c.relation));
  if (partner && partner.closeness < 25) {
    events.push({ t: s.minutes, kind: 'message', text: `${partner.name.split(' ')[0]}: I don't think this is working anymore.` });
    s.history.push({ t: s.minutes, text: `Broke up with ${partner.name}.` });
    partner.relation = 'Ex';
  }
  if (s.pregnancy && s.minutes >= s.pregnancy.due) {
    const r = rngFor(s, s.minutes);
    const boy = r.chance(0.5);
    const c = content(s);
    const names = c.names.find((g) => g.surname.includes(s.card.name.split(' ').slice(-1)[0])) ?? c.names[0];
    const name = `${r.pick(boy ? names.m : names.f)} ${s.card.name.split(' ').slice(-1)[0]}`;
    s.contacts.push({ name, relation: boy ? 'Son' : 'Daughter', closeness: 90, met: s.minutes });
    s.history.push({ t: s.minutes, text: `${name} was born.` });
    events.push({ t: s.minutes, kind: 'info', text: `${name.split(' ')[0]} was born. A ${boy ? 'boy' : 'girl'}.` });
    s.pregnancy = null;
  }
}

// --- legacy ------------------------------------------------------------------------------------------

export interface Legacy { heir: { name: string; relation: string } | null; estate: number; tax: number; inheritance: number; business: Business | null }

/** What's left, and who gets it. Children first, then a spouse, then siblings. */
export function legacyOf(s: LifeState): Legacy {
  const order = ['Son', 'Daughter', 'Wife', 'Husband', 'Brother', 'Sister'];
  const heir = s.contacts.filter((c) => order.includes(c.relation)).sort((a, b) => order.indexOf(a.relation) - order.indexOf(b.relation) || b.closeness - a.closeness)[0] ?? null;
  const businessValue = s.business ? s.business.boughtFor * 0.6 : 0;
  const estate = Math.max(0, s.money) + businessValue;
  const tax = Math.round(estate * 0.1);
  return { heir: heir ? { name: heir.name, relation: heir.relation } : null, estate, tax, inheritance: Math.max(0, Math.round(s.money - tax)), business: s.business };
}

/**
 * Everything that was theirs, the heir's: the house they owned (and how it was done up), the cars, the homes abroad,
 * the ventures, the land, the investments, the hotels, the bank account, what they'd bought (aircraft, jewellery,
 * clothes, gadgets), the paintings and the pets. (An heir used to get the cash and the business only: a player who
 * lost a character lost every car and house with it.)
 */
function inheritPossessions(s: LifeState, next: LifeState) {
  const from = s as LifeState & Record<string, unknown>, to = next as LifeState & Record<string, unknown>;
  for (const k of ['vehicles', 'homesAbroad', 'ventures', 'land', 'trade', 'hotel', 'paintings', 'pets', 'petFood']) if (from[k] !== undefined) to[k] = structuredClone(from[k]);
  next.owned = [...new Set([...(next.owned ?? []), ...(s.owned ?? [])])];
  if (s.bank) next.bank = structuredClone(s.bank);
  const hustle = (from.hustle as { gadgets?: string[] } | undefined)?.gadgets;
  if (hustle?.length) to.hustle = { skill: 0, heat: 0, heatAt: next.minutes, runs: 0, wins: 0, busts: 0, gadgets: [...hustle], cashed: 0 };
  // (their own house: the heir moves in; a rented place goes back to the landlord)
  if (s.homeOwned) {
    next.card.home = { ...s.card.home };
    next.homeOwned = true;
    next.homeValue = s.homeValue;
    next.homeStyle = s.homeStyle ? structuredClone(s.homeStyle) : next.homeStyle;
    next.rent = null;
  }
}

/** Continue as the heir: a new life, starting from what the last one left behind. */
export function continueAsHeir(s: LifeState, seed: number): LifeState | null {
  const l = legacyOf(s);
  if (!l.heir) return null;
  const sex = ['Son', 'Brother', 'Husband'].includes(l.heir.relation) ? 'm' : 'f';
  const child = l.heir.relation === 'Son' || l.heir.relation === 'Daughter';
  const card: LifeCard = generateLife({ region: s.card.region, stage: 'young', sex, age: 18 + (seed % (child ? 5 : 8)), name: l.heir.name, seed }); // (young too: nobody ages)
  const worth = l.inheritance + (l.business?.boughtFor ?? 0);
  card.background = {
    id: worth > 5_000_000 ? 'wealthy' : worth > 1_000_000 ? 'comfortable' : worth > 200_000 ? 'working' : 'struggling',
    label: 'Heir', family: `${s.card.name.split(' ')[0]} died at ${s.dead?.age ?? ageOf(s)}. You inherited ${naira(s, l.inheritance)}${l.business ? ` and the ${l.business.label.toLowerCase()}` : ''}.`,
  };
  card.money = Math.min(card.money, 120_000) + l.inheritance; // their own small savings, plus what they inherit
  const next = startLife(card);
  next.minutes = s.minutes - (s.minutes % DAY) + DAY + 8 * 60; // the morning after the funeral
  next.business = l.business ? { ...l.business, lastWorkedDay: -1 } : null;
  inheritPossessions(s, next);
  next.lineage = [...(s.lineage ?? []), { name: s.card.name, born: s.card.age, died: s.dead?.age ?? ageOf(s), cause: s.dead?.cause ?? '' }];
  next.history.unshift({ t: next.minutes, text: `Inherited ${naira(s, l.inheritance)} from ${s.card.name}.` });
  return next;
}
